import { useLayoutEffect, useState, type RefObject } from 'react';

/** Een maat van een element in px, bijgehouden met een ResizeObserver; `fallback` zolang er niets te meten valt (en in tests,
 * zonder ResizeObserver). Meet al bij de eerste weergave, zodat een paneel niet eerst verkeerd staat. */
function useSize(ref: RefObject<HTMLElement | null>, fallback: number, read: (el: HTMLElement) => number): number {
  const [size, setSize] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      setSize(fallback);
      return;
    }
    const measure = () => setSize(read(el) || fallback);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, fallback, read]);
  return size;
}

const readWidth = (el: HTMLElement) => el.clientWidth;
const readHeight = (el: HTMLElement) => el.clientHeight;

/** De breedte van een element in px (zie useSize). */
export function useWidth(ref: RefObject<HTMLElement | null>, fallback: number): number {
  return useSize(ref, fallback, readWidth);
}

/** De hoogte van een element in px (zie useSize); sinds ticket 28625 voor de lijst boven de lezer en de gevonden mails in het mailpaneel. */
export function useHeight(ref: RefObject<HTMLElement | null>, fallback: number): number {
  return useSize(ref, fallback, readHeight);
}
